type ProjectStatus = "PLANNED" | "IN_PROGRESS" | "LIVE" | "MAINTENANCE";

export type ProjectCard = {
  slug: string;
  title: string;
  tagline: string;
  status: ProjectStatus;
  impact: string;
  stack: string[];
  thumbnail: string;
  description: string;
  badges?: string[];
  metrics?: Array<{ label: string; value: string }>;
  liveUrl?: string;
  caseStudyUrl?: string;
  inProgress?: boolean;
  /** Carte « à venir » : pas d’URL live, lien vers les offres. */
  comingSoon?: boolean;
};

export const projects: ProjectCard[] = [
  {
    slug: "bazar-des-ecoles",
    title: "Bazar Des Écoles",
    tagline:
      "Site vitrine · Interface administrateur · Gestion produits & stocks · Design sur mesure",
    status: "LIVE",
    impact: "Catalogue clair et pilotage des stocks",
    stack: [
      "Site vitrine",
      "Interface administrateur",
      "Gestion de produits",
      "Gestion des stocks",
      "Design sur mesure",
      "Création de site",
    ],
    thumbnail: "/assets/main/projets/bazardesecoles.png",
    description:
      "Site vitrine sur mesure avec espace d’administration : gestion du catalogue produits, suivi des stocks et identité visuelle adaptée au projet.",
    badges: ["Site vitrine", "Admin"],
    metrics: [
      { label: "Back-office", value: "Produits & stocks" },
      { label: "Design", value: "Sur mesure" },
    ],
    liveUrl: "https://www.bazardesecoles.fr",
  },
  {
    slug: "star-limousine-paris",
    title: "Star Limousine Paris",
    tagline: "Location limousine Paris — site vitrine & réservation",
    status: "LIVE",
    impact: "Expérience premium et conversion",
    stack: ["Site vitrine", "SEO", "Réservation", "Performance"],
    thumbnail: "/assets/main/projets/couverture-star-limousine-wd.png",
    description:
      "Site pour une location de limousine à Paris : expériences, tarifs et réservation avec une identité forte.",
    liveUrl: "https://www.star-limousine-paris.com",
  },
  {
    slug: "jwl-marketing",
    title: "JWL Marketing",
    tagline: "Site Vitrine/E-commerce + SEO + mail automatiques",
    status: "LIVE",
    impact: "Site performant et optimisé",
    stack: ["Site Vitrine", "E-commerce", "SEO", "Mail automatiques"],
    thumbnail: "/assets/main/projets/couverture-jwl-wd.png",
    description:
      "Site vitrine et e-commerce avec optimisation SEO et automatisation des emails marketing.",
    badges: ["E-commerce", "SEO"],
    metrics: [
      { label: "Performance", value: "Optimisé" },
      { label: "SEO", value: "Actif" },
    ],
    liveUrl: "https://jwl-marketing-website.vercel.app",
  },
  {
    slug: "greenbeamcraft",
    title: "GreenBeamCraft",
    tagline: "Site E-commerce + Configurateur produit et aperçu 2D/3D + Mail automatiques + Interface administrateur",
    status: "LIVE",
    impact: "E-commerce avec configurateur avancé",
    stack: ["E-commerce", "Configurateur 2D/3D", "Mail automatiques", "Interface admin"],
    thumbnail: "/assets/main/projets/couverture-gbc-wd.png",
    description:
      "Site e-commerce avec configurateur de produit interactif, aperçu 2D/3D, automatisation des emails et interface d'administration complète.",
    badges: ["E-commerce", "Configurateur"],
    metrics: [
      { label: "Configurateur", value: "2D/3D" },
      { label: "Interface", value: "Admin complète" },
    ],
    liveUrl: "https://www.greenbeamcraft.com",
  },
  {
    slug: "multigestion-secretariat",
    title: "MultiGestion Secrétariat",
    tagline: "Secrétariat & gestion administrative pour entrepreneurs",
    status: "LIVE",
    impact: "Clarté de l’offre et prise de contact",
    stack: ["Site vitrine", "Offres", "Contact", "SEO"],
    thumbnail: "/assets/main/projets/couverture-multigestion-wd.png",
    description:
      "Site pour un service de secrétariat et gestion administrative : forfaits, expertise et prise de rendez-vous.",
    liveUrl: "https://www.multigestion-secretariat.fr",
  },
  {
    slug: "verdi-events",
    title: "Verdi Events",
    tagline:
      "Site vitrine 5 pages · SEO technique · Optimisation conversion · Parcours par étapes",
    status: "LIVE",
    impact: "Offre B2B lisible et parcours orienté conversion",
    stack: [
      "Site vitrine",
      "SEO technique",
      "Optimisation conversion",
      "Parcours par étapes",
    ],
    thumbnail: "/assets/main/projets/projet-verdi-events.png",
    description:
      "Site vitrine pour Verdi Events (fichiers B2B agricoles, emailing et prospection) : cinq pages, SEO technique, mise en avant de l’offre et système de parcours par étapes pour guider jusqu’au contact.",
    badges: ["Site vitrine", "SEO"],
    metrics: [
      { label: "Pages", value: "5" },
      { label: "Tunnel", value: "Par étapes" },
    ],
    liveUrl: "https://www.verdi-events.fr",
  },
  {
    slug: "signatures-france",
    title: "Signatures France",
    tagline:
      "Refonte · E-commerce · Abonnements et paiements · Interface administrateur",
    status: "LIVE",
    impact: "Marketplace du savoir-faire français, prête à vendre et à piloter",
    stack: [
      "Refonte",
      "E-commerce",
      "Abonnements & paiements",
      "Interface administrateur",
    ],
    thumbnail: "/assets/main/projets/couverture-signature-france.png",
    description:
      "Refonte et site e-commerce pour Signatures France : vitrine marketplace Made in France (marques, territoires, panier), flux d’abonnement et de paiement, et interface d’administration pour gérer catalogue, offres pros et contenus.",
    badges: ["E-commerce", "Admin"],
    metrics: [
      { label: "Projet", value: "Refonte" },
      { label: "Monétisation", value: "Abonnements" },
    ],
    liveUrl: "https://signaturesfrance.fr",
  },
  {
    slug: "portfolio-noah-descaves",
    title: "Portfolio Étudiant",
    tagline:
      "Site vitrine portfolio — parcours, expériences, compétences et projets",
    status: "LIVE",
    impact: "Présentation claire du profil et du parcours",
    stack: ["Site vitrine", "Design sur-mesure"],
    thumbnail: "/assets/main/projets/portfolio-noah-descaves.png",
    description:
      "Portfolio Étudiant pour Noah Descaves : site vitrine avec navigation par sections (parcours, expériences, compétences, projets), identité visuelle soignée et parcours de lecture fluide vers la prise de contact.",
    badges: ["Portfolio", "Design"],
    metrics: [
      { label: "Design", value: "Sur-mesure" },
      { label: "Performance", value: "Optimisé" },
    ],
    liveUrl: "https://www.noah-descaves.fr",
  },
];

/** Affichés uniquement sur `/projets` en plus des projets ci-dessus. */
export const portfolioPageExtraProjects: ProjectCard[] = [
  // Temporairement masqué — « Et pourquoi pas vous ? »
  // {
  //   slug: "exemples-bientot",
  //   title: "Et pourquoi pas vous ?",
  //   tagline: "Projets en cours de préparation",
  //   status: "IN_PROGRESS",
  //   impact: "—",
  //   stack: ["Refonte", "Création", "Automatisation", "Applications mobiles", "SEO"],
  //   thumbnail: "/futursiteproject.png",
  //   description:
  //     "Nous travaillons actuellement sur des projets, vous trouverez ici d'autres exemples très bientôt.",
  //   comingSoon: true,
  // },
];
